import useSWR from 'swr';
import { defaultFetcher } from '@fshr/shared';
import type { ContentV1 } from '@fshr/microservices/content';
import { getConfig } from '../config/config';
import { BookingV1 } from '@fshr/microservices/booking';

export interface WebChatContent {
	enabled: boolean;
	allowAnonymous: boolean;
	integrationId: string;
	propertyInfo: {
		name: string;
		nameIncludesHtml: boolean;
		image?: {
			url: string;
		};
		chatIcon?: string;
		propertyCode: string;
	};
	translations?: Array<{
		key: string;
		value: string;
	}>;
}

interface WebChatReturn {
	webChatContent: WebChatContent;
	booking: BookingV1.RoomReservation;
}

export function useWebChatContent(locale: string, propertyCode?: string, bookingId?: string) {
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();

	const buildUrl = () => {
		const baseUrl = `${NEXT_PUBLIC_BFF_BASE_URL}/webchat/?language=${locale}`;
		const owsParam = propertyCode ? `&owsCode=${propertyCode}` : '';
		const bookingParam = bookingId ? `&bookingId=${bookingId}` : '';
		return `${baseUrl}${owsParam}${bookingParam}`;
	};

	const { data, error, isLoading } = useSWR<WebChatReturn>(buildUrl(), defaultFetcher, {
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
	});

	const transformedData: ContentV1.WebChatResponse | undefined = data?.webChatContent
		? {
				...data?.webChatContent,
				propertyInfo: data?.webChatContent.propertyInfo
					? {
							...data?.webChatContent.propertyInfo,
							chatIcon: data?.webChatContent.propertyInfo.chatIcon || '',
							image: data?.webChatContent.propertyInfo.image
								? {
										url: data?.webChatContent.propertyInfo.image.url,
									}
								: undefined,
						}
					: undefined,
				translations: data?.webChatContent.translations || [],
			}
		: undefined;

	return {
		webChatContent: transformedData,
		bookingData: data?.booking,
		isLoading,
		error,
	};
}
